import type { CalendarEvent, LaneItem } from "@ondetatu/shared";
import { useEffect, useMemo, useState } from "react";
import { type Circle, circlesApi } from "../../lib/api/sharing.js";
import { viewsApi } from "../../lib/api/views.js";
import { Lanes } from "../lanes/Lanes.js";
import "./Calendar.css";

const TYPE_COLOR: Record<string, string> = {
  trip: "var(--clay)",
  vacation: "var(--amber)",
  nomadic: "var(--teal)",
  away: "var(--muted)",
};

function toLaneItems(events: CalendarEvent[]): LaneItem[] {
  return events.map((e) => ({
    id: e.id,
    person_id: e.user_id,
    person_name: e.name,
    city: e.city,
    country: e.country,
    country_code: e.country_code,
    date_from: e.start,
    date_to: e.end,
    type: e.type,
  }));
}

export function CalendarView() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [view, setView] = useState<"agenda" | "lanes">("agenda");
  const [who, setWho] = useState("all");
  const [circles, setCircles] = useState<Circle[]>([]);
  const [scopeCircle, setScopeCircle] = useState("all");
  const [circleMembers, setCircleMembers] = useState<Set<string> | null>(null);

  useEffect(() => {
    viewsApi.calendar().then((r) => setEvents(r.success && r.data ? r.data.events : []));
    circlesApi.list().then((r) => setCircles(r.success && r.data ? r.data.circles : []));
  }, []);

  useEffect(() => {
    if (scopeCircle === "all") {
      setCircleMembers(null);
      return;
    }
    circlesApi.members(scopeCircle).then((r) => {
      setCircleMembers(r.success && r.data ? new Set(r.data.members.map((m) => m.id)) : new Set());
    });
  }, [scopeCircle]);

  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of events) map.set(e.user_id, e.name);
    return [...map.entries()];
  }, [events]);

  const agenda = useMemo(() => {
    const list = who === "all" ? events : events.filter((e) => e.user_id === who);
    return [...list].sort((a, b) => (a.start < b.start ? -1 : 1));
  }, [events, who]);

  const laneItems = useMemo(() => {
    const scoped = circleMembers ? events.filter((e) => circleMembers.has(e.user_id)) : events;
    return toLaneItems(scoped);
  }, [events, circleMembers]);

  const scopePicker = (
    <select aria-label="scope" value={scopeCircle} onChange={(e) => setScopeCircle(e.target.value)}>
      <option value="all">Everyone</option>
      {circles.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );

  return (
    <section className="cal">
      <div className="cal__head">
        <h1 className="cal__h1">Calendar</h1>
        <fieldset className="cal__viewtoggle" aria-label="view">
          <button
            type="button"
            className={view === "agenda" ? "on" : ""}
            onClick={() => setView("agenda")}
          >
            Agenda
          </button>
          <button
            type="button"
            className={view === "lanes" ? "on" : ""}
            onClick={() => setView("lanes")}
          >
            Lanes
          </button>
        </fieldset>
      </div>

      {view === "agenda" ? (
        <>
          <select
            className="cal__filter"
            aria-label="filter by person"
            value={who}
            onChange={(e) => setWho(e.target.value)}
          >
            <option value="all">Everyone</option>
            {people.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          {agenda.length === 0 && <p className="cal__muted">No upcoming travel to show.</p>}
          <ol className="cal__list">
            {agenda.map((e) => (
              <li key={e.id} className="cal__event" data-entry-type={e.type}>
                <span
                  className="cal__dot"
                  style={{ background: TYPE_COLOR[e.type] ?? "var(--muted)" }}
                />
                <span className="cal__event-main">
                  <span className="cal__event-who">
                    <b>{e.name}</b> — <span className="cal__event-title">{e.title}</span>
                  </span>
                  <span className="cal__event-dates">
                    {e.start} → {e.end ?? "ongoing"} · {e.type}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <Lanes items={laneItems} scope={scopePicker} />
      )}
    </section>
  );
}
